import {Form, Formik} from 'formik';
import invoke from 'lodash.invoke';
import appLogger from '@core/LoggingProvider';
import ProgressBar from '../ProgressBar';
import {Button, Message, SelectField, TextField} from '@ultradent/components';
import {formatPhoneUS, trimFormData} from '@ultradent/utilities/Strings';
import Validator from '@util/Validator';
import RegistrationService from '@services/RegistrationService';
import Icon from '@components/common/Icon';
import {usStateList} from '@util/USStateList';

const specialtyList = [
    {
        label: 'General Practice',
        value: 'General Practice'
    }, {
        label: 'Endodontics',
        value: 'Endodontics'
    }, {
        label: 'Oral Surgeon',
        value: 'Oral Surgeon'
    }, {
        label: 'Pediatric Dentist',
        value: 'Pediatric Dentist'
    }, {
        label: 'Periodontist',
        value: 'Periodontist'
    }, {
        label: 'Orthodontist',
        value: 'Opal Orthodontist (OPAL Only)'
    }, {
        label: 'Dental Hygiene School',
        value: 'Non-Univ Dental Hygiene School'
    }, {
        label: 'Dental Assisting School',
        value: 'Non-Univ Dental Assisting School'
    }];

const formValidator = new Validator( {
    practiceName: {
        required: true
    },
    officePhone: {
        required: true,
        phoneNumber: {
            locale: 'en-US'
        }
    },
    address1: {
        required: true
    },
    city: {
        required: true
    },
    state: {
        required: true
    },
    postalCode: {
        required: true,
        postalCode: {
            locale: 'US'
        }
    }
} );

const OfficeDetails = ( props, context ) =>
    <div>
        <ProgressBar step={'2'} completed={[1, 0, 0]} labels={['create login', 'office details', 'verify account']}/>

        <h3 className="leading-tight text-center md:text-lg font-normal regSubHeading mx-auto">We require a valid U.S.
            dental license, phone number, and address to verify that you are a dentist.</h3>
        <p className="regSubHeading leading-tight txt-c2 text-center text-xs md:text-sm mt-2 mx-auto">
            If your practice is considered a <strong>Large Group, University, or Government account</strong>,
            please call us at <a href={`tel:${upi.conf.phoneTollFree}`}
                                 className="link-2">{upi.conf.phoneTollFree}</a> to setup an online account.
        </p>

        <div className="universalForm-container my-4 mx-auto">
            <Formik
                validate={formData => {
                    const {
                        practiceName,
                        officePhone,
                        address1,
                        city,
                        state,
                        postalCode
                    } = trimFormData( context.state );

                    return formValidator.validate( {practiceName, officePhone, address1, city, state, postalCode} );
                }}
                onSubmit={( values, {setStatus, setSubmitting} ) => {
                    values.loading = true;
                    const {address1, address2, city, state, postalCode} = context.state;

                    const addressInput = {
                        address1,
                        address2,
                        city,
                        state,
                        postalCode
                    };
                    RegistrationService.validateAddress( {address: addressInput} )
                        .then( resp => {
                            const {county} = resp.candidates[0] || '';

                            invoke( context, 'setCounty', county );

                            if ( !resp.exactMatch && resp.candidates.length === 0 ) {
                                appLogger.triggerEvent( {
                                    message: 'Registration: Invalid Address',
                                    extra: addressInput
                                } );
                                setSubmitting( false );
                                values.loading = false;
                                setStatus( {serverError: 'The address you provided is not valid, please check your entry and try again.'} );
                            }
                            else {
                                values.loading = false;
                                if ( resp.exactMatch || resp.candidates.length === 0 ) {
                                    const {
                                        emailAddress,
                                        password,
                                        dentalLicense,
                                        dentistFullName,
                                        practiceName,
                                        specialty,
                                        officePhone,
                                        address1,
                                        address2,
                                        city,
                                        state,
                                        postalCode
                                    } = context.state;

                                    const registerNewCustomerInput = {
                                        dentistFullName,
                                        practiceName,
                                        specialty,
                                        officePhone: formatPhoneUS( officePhone ),
                                        dentalLicense,
                                        officeAddress: {
                                            address1,
                                            address2,
                                            city,
                                            state,
                                            postalCode,
                                            ...(county && {county})
                                        },
                                        emailAddress,
                                        password
                                    };

                                    RegistrationService.registerNewCustomer( {input: registerNewCustomerInput} )
                                        .then( resp => {
                                            const {registerNewCustomer} = resp.data;
                                            values.loading = false;
                                            if ( registerNewCustomer ) {
                                                props.history.push( {
                                                    pathname: '/accountVerifiedNPI',
                                                    state: {isSubmitted: true, emailAddress}
                                                } );
                                            }
                                            else {
                                                props.history.push( {
                                                    pathname: '/accountRequiresVerification',
                                                    state: {isSubmitted: true, emailAddress}
                                                } );
                                            }
                                        } )
                                        .catch( error => {
                                            appLogger.capture( error );
                                            setSubmitting( false );
                                            values.loading = false;
                                            console.log( error );
                                            setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                                        } );
                                }
                                else {
                                    invoke( context, 'setUserAddresses', resp.candidates );
                                    props.history.push( '/notValidated' );
                                }
                            }
                        } )
                        .catch( error => {
                            appLogger.capture( error );
                            values.loading = false;
                            setSubmitting( false );
                            console.log( error );
                            setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                        } );
                }}
                initialValues={{
                    loading: false
                }}
                render={( {values, errors, status, touched, handleSubmit, setFieldValue, isSubmitting} ) => (
                    <Form className="my-4 universalForm">
                        {status &&
                            <Message notice
                                     className="flex font-normal items-center -ml-4 -mr-4 md:-ml-8 md:-mr-8 px-4 md:px-8 py-4 text-white w-auto">
                                <Icon.ui wrapperClassName="mr-2" className="text-white text-xl"
                                         name="report-problem-triangle"/>
                                <span className="message-bar-msg">{status.serverError || status.email}</span>
                            </Message>
                        }
                        <p className="mt-4 text-xs">* indicates a required field.</p>
                        <TextField
                            type="text"
                            name="practiceName"
                            label="Name of Practice*"
                            value={context.state.practiceName}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.practiceName}
                            id="practiceName"/>
                        <SelectField
                            id="specialty"
                            label="Specialty*"
                            name="specialty"
                            onChange={context.handleChange}
                            error={errors.state}
                            value={context.state.specialty}
                            className="w-full">
                            {specialtyList.map( ( {label, value} ) => <option value={value}>{label}</option> )}
                        </SelectField>
                        <TextField
                            type="tel"
                            name="officePhone"
                            label="Phone Number*"
                            value={context.state.officePhone}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.officePhone}
                            id="officePhone"/>

                        <label className="-mb-2 font-bold" htmlFor="address1">Shipping address</label>
                        <TextField
                            type="text"
                            name="address1"
                            label="Street*"
                            value={context.state.address1}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.address1}
                            id="address1"/>
                        <TextField
                            type="text"
                            name="address2"
                            label="Apt, Suite, etc."
                            value={context.state.address2}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.address2}
                            id="address2"/>
                        <TextField
                            type="text"
                            name="city"
                            label="City*"
                            value={context.state.city}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.city}
                            id="city"/>
                        <div className="flex justify-between">
                            <div className="mr-2 w-1/2">
                                <SelectField
                                    id="state"
                                    label="State*"
                                    name="state"
                                    onChange={context.handleChange}
                                    error={errors.state}
                                    value={context.state.state}
                                    className="w-full">
                                    <option selected="selected" value="">Select one</option>
                                    {usStateList.map(
                                        st => <option value={st.abbreviation}>{st.abbreviation}</option> )}
                                </SelectField>
                            </div>
                            <div className="w-1/2">
                                <TextField
                                    type="text"
                                    name="postalCode"
                                    label="ZIP Code*"
                                    value={context.state.postalCode}
                                    onChange={context.handleChange}
                                    inputClassName="w-full"
                                    error={errors.postalCode}
                                    id="postalCode"/>
                            </div>
                        </div>

                        <div className="button-bar flex-row mt-8 mx-0">
                            <span className="button tertiary"
                                  onClick={context.handleGoBack}
                                  style="margin-left: 0 !important;">Back</span>
                            <Button loading={values.loading}
                                    disabled={isSubmitting}
                                    primary
                                    onSubmit={handleSubmit}
                                    style="margin-right: 0 !important;">Next</Button>
                        </div>
                    </Form>
                )}/>
        </div>
    </div>;

export default OfficeDetails;